import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
    append, iif, insertItem, patch, removeItem, updateItem
} from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Laboratory } from 'src/app/services/dtos/laboratory';
import { LaboratoryBasicInfo } from 'src/app/services/dtos/laboratoryBasicInfo';
import { LaboratoriesService } from 'src/app/services/laboratories.service';
import {
    AddLaboratory,
    AddLaboratoryConnection,
    AddLaboratoryEmailTemplate,
    AddLaboratoryEsb,
    ClearCurrentLaboratoryId,
    DeleteLaboratory,
    DeleteLaboratoryConnection,
    DeleteLaboratoryEmailTemplate,
    DeleteLaboratoryEsb,
    DeleteLaboratoryLogs,
    DeleteLaboratoryTasks,
    FetchAllLaboratoriesBasicInfo,
    FetchCurrentUserLaboratoriesBasicInfo,
    FetchLaboratory, FetchTemplateTypesIfEmpty, SetCurrentLaboratoryId,
    UpdateLaboratory,
    UpdateLaboratoryConnection,
    UpdateLaboratoryEmailTemplate,
    UpdateLaboratoryEsb
} from './laboratories.actions';

export class LaboratoriesStateModel {
  public laboratories!: Laboratory[];
  public currentUserLaboratories!: Laboratory[];
  public selectedLaboratoryId!: string;
  public templateTypes!: string[];
  public allLaboratories!: Laboratory[];
}

const defaults = {
  laboratories: [],
  currentUserLaboratories: [],
  selectedLaboratoryId: '',
  templateTypes: [],
  allLaboratories: [],
};

@State<LaboratoriesStateModel>({
  name: 'laboratoriesState',
  defaults,
})
@Injectable()
export class LaboratoriesState {
  public constructor(private laboratoriesService: LaboratoriesService) {}

  @Selector()
  public static getSelectedLaboratoryId(
    state: LaboratoriesStateModel
  ): string {
    return state.selectedLaboratoryId;
  }

  @Selector()
  public static getSelectedLaboratory(
    state: LaboratoriesStateModel
  ): Laboratory | undefined {
    return state.laboratories.find((c) => c.id === state.selectedLaboratoryId);
  }

  @Selector()
  public static getLaboratoriesBasicInfo(
    state: LaboratoriesStateModel
  ): LaboratoryBasicInfo[] {
    return state.laboratories
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name)) as LaboratoryBasicInfo[];
  }

  @Selector()
  public static getCurrentUserLaboratoriesBasicInfo(
    state: LaboratoriesStateModel
  ): LaboratoryBasicInfo[] {
    return state.currentUserLaboratories as LaboratoryBasicInfo[];
  }

  @Selector()
  public static getTemplateTypes(state: LaboratoriesStateModel): string[] {
    return state.templateTypes;
  }

  @Action(FetchTemplateTypesIfEmpty)
  public fetchAllTemplateTypes({
    getState,
    setState,
  }: StateContext<LaboratoriesStateModel>): Observable<string[]> | void {
    if (getState().templateTypes.length === 0) {
      return this.laboratoriesService.getAllTemplateTypes().pipe(
        tap((templateTypes: string[]) => {
          const state = getState();
          setState({ ...state, templateTypes });
        })
      );
    }
  }

  @Action(SetCurrentLaboratoryId)
  public setCurrentLaboratoryId(
    { setState }: StateContext<LaboratoriesStateModel>,
    { id }: SetCurrentLaboratoryId
  ): void {
    setState(
      patch({
        selectedLaboratoryId: id,
      })
    );
  }

  @Action(ClearCurrentLaboratoryId)
  public clearCurrentLaboratoryId({
    setState,
  }: StateContext<LaboratoriesStateModel>): void {
    setState(
      patch({
        selectedLaboratoryId: '',
      })
    );
  }

  @Action(FetchAllLaboratoriesBasicInfo)
  public fetchAll({
    getState,
    setState,
  }: StateContext<LaboratoriesStateModel>): Observable<Laboratory[]> {
    return this.laboratoriesService.getAllBasicInfo().pipe(
      tap((freshLaboratories: Laboratory[]) => {
        const state = getState();
        // add or update
        freshLaboratories.forEach((laboratory) => {
          setState(
            patch<LaboratoriesStateModel>({
              laboratories: iif<Laboratory[]>(
                (laboratories) =>
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  laboratories!.some((x) => x.id === laboratory.id),
                updateItem<Laboratory>(
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  (x) => x!.id === laboratory.id,
                  laboratory
                ),
                insertItem<Laboratory>(laboratory)
              ),
            })
          );
        });
        // remove
        const laboratoryToRemove = state.laboratories.filter((c) =>
          freshLaboratories.every((fc) => fc.id !== c.id)
        );
        laboratoryToRemove.forEach((laboratory) => {
          setState(
            patch<LaboratoriesStateModel>({
              laboratories: removeItem<Laboratory>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (x) => x!.id === laboratory.id
              ),
            })
          );
        });
      })
    );
  }

  @Action(FetchCurrentUserLaboratoriesBasicInfo)
  public fetchCurrentUser({
    setState,
  }: StateContext<LaboratoriesStateModel>): Observable<Laboratory[]> {
    return this.laboratoriesService.getCurrentUserBasicInfo().pipe(
      tap((freshLaboratories: Laboratory[]) => {
        setState(
          patch({
            currentUserLaboratories: freshLaboratories,
          })
        );
      })
    );
  }

  @Action(FetchLaboratory)
  public fetchSingle(
    { setState }: StateContext<LaboratoriesStateModel>,
    { id }: FetchLaboratory
  ): Observable<Laboratory> {
    return this.laboratoriesService.getLaboratory(id).pipe(
      tap((laboratory: Laboratory) => {
        setState(
          patch<LaboratoriesStateModel>({
            laboratories: iif<Laboratory[]>(
              (laboratories) =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                laboratories!.some((x) => x.id === laboratory.id),
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              updateItem<Laboratory>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (x) => x!.id === laboratory.id,
                laboratory
              ),
              insertItem<Laboratory>(laboratory)
            ),
          })
        );
      })
    );
  }

  @Action(UpdateLaboratory)
  public update(
    { setState, getState }: StateContext<LaboratoriesStateModel>,
    payload: UpdateLaboratory
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any> {
    return this.laboratoriesService
      .updateLaboratory(payload.laboratoryBasicInfoForm)
      .pipe(
        tap(() => {
          const state = getState();
          const laboratory = state.laboratories.find(
            (x) => x.id === payload.laboratoryBasicInfoForm.id
          );
          const updated = {
            ...laboratory,
            code: payload.laboratoryBasicInfoForm.code,
            name: payload.laboratoryBasicInfoForm.name,
            regionId: payload.laboratoryBasicInfoForm.regionId,
            remarks: payload.laboratoryBasicInfoForm.remarks,
            eMailAddress: payload.laboratoryBasicInfoForm.eMailAddress,
            logDeleteDays: payload.laboratoryBasicInfoForm.logDeleteDays,
            taskDeleteDays: payload.laboratoryBasicInfoForm.taskDeleteDays
          } as Laboratory;
          setState(
            patch({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, max-len
              laboratories: updateItem<Laboratory>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (x) => x!.id === payload.laboratoryBasicInfoForm.id,
                updated
              ),
            })
          );
        })
      );
  }

  @Action(DeleteLaboratory)
  public delete(
    { setState }: StateContext<LaboratoriesStateModel>,
    { id }: DeleteLaboratory
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.laboratoriesService.deleteLaboratory(id).pipe(
      tap(() => {
        setState(
          patch({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            laboratories: removeItem<Laboratory>((x) => x!.id === id),
          })
        );
      })
    );
  }

  @Action(DeleteLaboratoryLogs)
  public deleteLogs(
    _ : StateContext<LaboratoriesStateModel>,
    { id }: DeleteLaboratoryLogs
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.laboratoriesService.deleteLaboratoryLogs(id);
  }

  @Action(DeleteLaboratoryTasks)
  public deleteTasks(
    _ : StateContext<LaboratoriesStateModel>,
    { id }: DeleteLaboratoryLogs
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.laboratoriesService.deleteLaboratoryTasks(id);
  }

  @Action(AddLaboratory)
  public add(
    { setState }: StateContext<LaboratoriesStateModel>,
    payload: AddLaboratory
  ): Observable<string> {
    return this.laboratoriesService
      .addLaboratory(payload.laboratoryBasicInfoForm)
      .pipe(
        tap((laboratoryId) => {
          const newLaboratory = {
            ...payload.laboratoryBasicInfoForm,
            id: laboratoryId,
            customers: 0,
          } as Laboratory;
          setState(
            patch({
              laboratories: append<Laboratory>([newLaboratory]),
              selectedLaboratoryId: laboratoryId,
            })
          );
        })
      );
  }

  @Action(UpdateLaboratoryConnection)
  public updateLaboratoryConnection(
    { dispatch }: StateContext<LaboratoriesStateModel>,
    { payload }: UpdateLaboratoryConnection
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  Observable<any> {
    return this.laboratoriesService
      .updateLaboratoryConnection(payload)
      .pipe(tap(() => dispatch(new FetchLaboratory(payload.laboratoryId))));
  }

  @Action(DeleteLaboratoryConnection)
  public deleteLaboratoryConnection(
    { dispatch }: StateContext<LaboratoriesStateModel>,
    { laboratoryId, connectionId }: DeleteLaboratoryConnection
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  Observable<any> {
    return this.laboratoriesService
      .deleteLaboratoryConnection(laboratoryId, connectionId)
      .pipe(tap(() => dispatch(new FetchLaboratory(laboratoryId))));
  }

  @Action(AddLaboratoryConnection)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public addLaboratoryConnection(
    { dispatch }: StateContext<LaboratoriesStateModel>,
    { payload }: AddLaboratoryConnection
  ): Observable<string> {
    return this.laboratoriesService
      .addLaboratoryConnection(payload)
      .pipe(tap(() => dispatch(new FetchLaboratory(payload.laboratoryId))));
  }

  @Action(UpdateLaboratoryEmailTemplate)
  public updateLaboratoryEmailTemplate(
    { dispatch }: StateContext<LaboratoriesStateModel>,
    { payload }: UpdateLaboratoryEmailTemplate
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  Observable<any> {
    return this.laboratoriesService
      .updateLaboratoryEmailTemplate(payload)
      .pipe(tap(() => dispatch(new FetchLaboratory(payload.laboratoryId))));
  }

  @Action(DeleteLaboratoryEmailTemplate)
  public deleteLaboratoryEmailTemplate(
    { dispatch }: StateContext<LaboratoriesStateModel>,
    { laboratoryId, templateId }: DeleteLaboratoryEmailTemplate
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  Observable<any> {
    return this.laboratoriesService
      .deleteLaboratoryEmailTemplate(laboratoryId, templateId)
      .pipe(tap(() => dispatch(new FetchLaboratory(laboratoryId))));
  }

  @Action(AddLaboratoryEmailTemplate)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public addLaboratoryEmailTemplate(
    { dispatch }: StateContext<LaboratoriesStateModel>,
    { payload }: AddLaboratoryEmailTemplate
  ): Observable<string> {
    return this.laboratoriesService
      .addLaboratoryEmailTemplate(payload)
      .pipe(tap(() => dispatch(new FetchLaboratory(payload.laboratoryId))));
  }

  @Action(DeleteLaboratoryEsb)
  public deleteRegionConnection(
    { dispatch }: StateContext<LaboratoriesStateModel>,
    { laboratoryId, esbId: esbId }: DeleteLaboratoryEsb
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  Observable<any> {
    return this.laboratoriesService
      .deleteLaboratoryEsb(laboratoryId, esbId)
      .pipe(tap(() => dispatch(new FetchLaboratory(laboratoryId))));
  }

  @Action(AddLaboratoryEsb)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public addRegionEsb(
    { dispatch }: StateContext<LaboratoriesStateModel>,
    { payload }: AddLaboratoryEsb
  ): Observable<string> {
    return this.laboratoriesService
      .addLaboratoryEsb(payload)
      .pipe(tap(() => dispatch(new FetchLaboratory(payload.locationId))));
  }

  @Action(UpdateLaboratoryEsb)
  public updateRegionEsb(
    { dispatch }: StateContext<LaboratoriesStateModel>,
    { payload }: UpdateLaboratoryEsb
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  Observable<any> {
    return this.laboratoriesService
      .updateLaboratoryEsb(payload)
      .pipe(tap(() => dispatch(new FetchLaboratory(payload.locationId))));
  }
}
